import React from 'react'
import { Box, Typography, makeStyles, Theme } from '@material-ui/core'

import { RENEWAL_BOARD_TABS, RenewalsBoardStage } from './renewalsBoard'

const STAGE_COLOR: Record<RenewalsBoardStage, string> = {
    eligible: '#6b7280',
    draft: '#9ca3af',
    offered: '#6570E6',
    transfer: '#b45309',
    signing: '#d97706',
    future_resident: '#047857',
    lost: '#be123c',
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        flex: '1 1 360px',
        minWidth: 280,
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 1.25),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
    },
    rows: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 2,
        width: '100%',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '86px 1fr 28px',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0, 0.25),
        border: 'none',
        borderRadius: 4,
        backgroundColor: 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        minHeight: 12,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    rowActive: {
        backgroundColor:
            theme.palette.type === 'dark'
                ? 'rgba(25, 118, 210, 0.12)'
                : 'rgba(25, 118, 210, 0.06)',
        '&:hover': {
            backgroundColor:
                theme.palette.type === 'dark'
                    ? 'rgba(25, 118, 210, 0.16)'
                    : 'rgba(25, 118, 210, 0.1)',
        },
    },
    label: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        lineHeight: 1.15,
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    labelActive: {
        color: theme.palette.text.primary,
    },
    track: {
        height: 4,
        borderRadius: 999,
        backgroundColor: theme.palette.action.hover,
        overflow: 'hidden',
    },
    fill: {
        height: '100%',
        borderRadius: 999,
        minWidth: 0,
        transition: 'width 160ms ease',
    },
    count: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: 1.15,
        fontFamily: 'monospace',
        textAlign: 'right',
        color: theme.palette.text.primary,
    },
}))

interface RenewalsFunnelProps {
    counts: Record<RenewalsBoardStage, number>
    searchCounts?: Record<string, number> | null
    activeStage: RenewalsBoardStage
    loading?: boolean
    onStageChange: (stage: RenewalsBoardStage) => void
}

export const RenewalsFunnel: React.FC<RenewalsFunnelProps> = ({
    counts,
    searchCounts,
    activeStage,
    loading = false,
    onStageChange,
}) => {
    const classes = useStyles()
    const values = RENEWAL_BOARD_TABS.map((tab) => {
        const stage = tab.id as RenewalsBoardStage
        if (searchCounts) return searchCounts[stage] ?? 0
        return counts[stage] ?? 0
    })
    const maxValue = Math.max(...values, 1)

    return (
        <Box className={classes.root} aria-label="Renewal funnel">
            <Box className={classes.rows}>
                {RENEWAL_BOARD_TABS.map((tab, index) => {
                    const stage = tab.id as RenewalsBoardStage
                    const value = values[index]
                    const widthPct =
                        value <= 0 ? 0 : Math.max(4, (value / maxValue) * 100)
                    const isActive = activeStage === stage

                    return (
                        <Box
                            key={stage}
                            className={`${classes.row} ${
                                isActive ? classes.rowActive : ''
                            }`}
                            role="button"
                            tabIndex={0}
                            onClick={() => onStageChange(stage)}
                            onKeyDown={(event) => {
                                if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                ) {
                                    event.preventDefault()
                                    onStageChange(stage)
                                }
                            }}
                        >
                            <Typography
                                className={`${classes.label} ${
                                    isActive ? classes.labelActive : ''
                                }`}
                            >
                                {tab.id === 'future_resident'
                                    ? 'Signed'
                                    : tab.label}
                            </Typography>
                            <Box className={classes.track}>
                                <Box
                                    className={classes.fill}
                                    style={{
                                        width: loading ? 0 : `${widthPct}%`,
                                        backgroundColor: STAGE_COLOR[stage],
                                    }}
                                />
                            </Box>
                            <Typography className={classes.count}>
                                {loading ? '—' : value}
                            </Typography>
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}
